var _effectorFileName = "/builds/gmini/front-ui-kit/src/VirtualizedAutocomplete/VirtualizedAutocomplete.tsx";
import * as React from 'react';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import useMediaQuery from '@mui/material/useMediaQuery';
import ListSubheader from '@mui/material/ListSubheader';
import Popper from '@mui/material/Popper';
import { useTheme, styled } from '@mui/material/styles';
import { VariableSizeList } from 'react-window';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import { jsx as _jsx } from "react/jsx-runtime";
const LISTBOX_PADDING = 8; // px

function renderRow(props) {
  const {
    data,
    index,
    style
  } = props;
  const dataSet = data[index];
  const inlineStyle = { ...style,
    top: style.top + LISTBOX_PADDING
  };

  if (Object.prototype.hasOwnProperty.call(dataSet, 'group')) {
    return /*#__PURE__*/_jsx(ListSubheader, {
      component: "div",
      style: inlineStyle,
      children: dataSet.group
    }, dataSet.key);
  }

  return /*#__PURE__*/React.cloneElement(data[index], {
    style: { ...style,
      top: style.top + LISTBOX_PADDING
    }
  });
}

const OuterElementContext = /*#__PURE__*/React.createContext({});
const OuterElementType = /*#__PURE__*/React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return /*#__PURE__*/_jsx("div", {
    ref: ref,
    ...props,
    ...outerProps
  });
});
OuterElementType.displayName = 'OuterElementType';

function useResetCache(data) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
} // Adapter for react-window


const ListboxComponent = /*#__PURE__*/React.forwardRef(function ListboxComponent(props, ref) {
  const {
    children,
    ...other
  } = props;
  const itemData = [];
  children.forEach(item => {
    itemData.push(item);
    itemData.push(...(item.children || []));
  });
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
    noSsr: true
  });
  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 48;

  const getChildSize = child => {
    if (Object.prototype.hasOwnProperty.call(child, 'group')) {
      return 48;
    }

    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }

    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);
  return /*#__PURE__*/_jsx("div", {
    ref: ref,
    children: /*#__PURE__*/_jsx(OuterElementContext.Provider, {
      value: other,
      children: /*#__PURE__*/_jsx(VariableSizeList, {
        itemData: itemData,
        height: getHeight() + 2 * LISTBOX_PADDING,
        width: "100%",
        ref: gridRef,
        outerElementType: OuterElementType,
        innerElementType: "ul",
        itemSize: index => getChildSize(itemData[index]),
        overscanCount: 5,
        itemCount: itemCount,
        children: renderRow
      })
    })
  });
});
const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0
    }
  }
});
export function VirtualizedAutocomplete({
  classes: customClasses,
  renderOption,
  ...otherProps
}) {
  const classes = useStyles();
  const autocompleteClasses = useAutocompleteStyles();
  return /*#__PURE__*/_jsx(Autocomplete, {
    classes: { ...autocompleteClasses,
      ...customClasses
    },
    disableListWrap: true,
    PopperComponent: StyledPopper,
    openOnFocus: true,
    ListboxComponent: ListboxComponent,
    renderOption: (props, option, state, ownerState) => /*#__PURE__*/_jsx(Typography, { ...props,
      noWrap: true,
      className: classes.optionTypography,
      children: renderOption ? renderOption(props, option, state, ownerState) : otherProps.getOptionLabel ? otherProps.getOptionLabel(option) : option
    }),
    ...otherProps
  });
}
const useAutocompleteStyles = makeStyles({
  listbox: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0
    }
  }
});
const useStyles = makeStyles({
  optionTypography: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '10px 8px 10px 12px',
    cursor: 'pointer',
    '&:hover, &.Mui-focused': {
      background: 'rgba(53, 59, 96, 0.05)'
    },
    '&[aria-selected="true"]': {
      background: 'rgba(92, 130, 229, 0.15)'
    },
    [`&.Mui-focused[aria-selected="true"]`]: {
      background: 'linear-gradient(rgba(53, 59, 96, 0.05), rgba(53, 59, 96, 0.05)), linear-gradient(rgba(92, 130, 229, 0.15), rgba(92, 130, 229, 0.15))'
    }
  }
});